import React, { useState } from 'react';
import PropTypes from 'prop-types';
// components
import WidgetButtons from './WidgetButtons';
import CustomWidgetCard from './CustomWidgetCard';
// redux
import { useSelector } from '../../redux/store';
// hooks
import { useToggle } from '../../hooks';
// utils
import groupedTreatments from '../../utils/groupedTreatments';
import { resolveWidgetTreatments } from '../../utils/widgetTreatmentMatching';

const AttendedTableReportsWidget = ({ onWidgetClick, dateRangeProps, widgetToggle, onContactedCountFilter = null }) => {
    const { toggle: internalToggle, onOpen, onClose } = useToggle(true);
    const toggle = widgetToggle !== undefined ? widgetToggle : internalToggle;
    const { treatmentsList: { results } } = useSelector((state) => state.practiceTreatment);

    const [currentWidget, setCurrentWidget] = useState('');

    const treatments = groupedTreatments(results);

    const handleOpen = () => onOpen();

    const handleClose = () => onClose();

    const widgetArray = [
        // ISS-041: this card's NUMBER is attended leads with zero contact attempts logged
        // since attending — ATTENDED_TABLE_WIDGET_COUNTER key '1' is
        // contactedCountForStatus: '0,exact,CONSULTATION_ATTENDED' (constants/newLead.js).
        // `value: '0'` is NOT a treatment: it sits in the treatment slot only because this
        // widget array is treatment-shaped, which is exactly why the click used to be read
        // as "no treatments selected" and cleared the treatment filter instead.
        // contactedCountValue carries the real scope; see handleWidgetClick below.
        { key: '1', icon: '', value: '0', text: 'Consultation follow ups', contactedCountValue: '0' },
        { key: '2', icon: '', value: 'invisalign', text: 'Consultation attended for Invisalign', isTreatment: true },
        { key: '3', icon: '', value: 'bonding', text: 'Consultation attended for Bonding', isTreatment: true },
        { key: '4', icon: '', value: 'implants', text: 'Consultation attended for Implants', isTreatment: true },
        { key: '5', icon: '', value: 'whitening', text: 'Consultation attended for Whitening', isTreatment: true },
        { key: '6', icon: '', value: '6', text: 'Estimated value of consultations attended', isPrice: true },
    ];

    const normaliseValues = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);

    const handleWidgetClick = (value, key) => {
        const card = widgetArray.find((c) => c.key === key);
        // ISS-041 — apply the scope the card's own count is derived from.
        //
        // Before: setCurrentWidget(key) painted the card selected, then `value: '0'` fell
        // through the "every value is 0" branch to onWidgetClick([]), which CLEARS the
        // treatment filter. So the card advertised itself as an active filter over a table
        // that had just been un-filtered — the same "selecting a filter shows all the data"
        // confusion as ISS-036, reached from the opposite direction.
        //
        // The matching table filter already existed (buildAttendedCountFilterQuery('0') ->
        // &contacted_count_for_status=0,exact,CONSULTATION_ATTENDED, the same predicate the
        // count uses) and already had a route to the table via directCountFilter, so this is
        // plumbing rather than new behaviour. Kept clickable deliberately: the practices that
        // see this legacy widget are the ones that do NOT get the contextual widget's Due Now
        // card, so it is their only actionable card — making it display-only would have cost
        // them the worklist.
        if (card?.contactedCountValue != null && onContactedCountFilter) {
            setCurrentWidget(key);
            onContactedCountFilter(card.contactedCountValue);
            return;
        }
        setCurrentWidget(key);
        // Selecting any OTHER card must release the contacted-count scope, or
        // ConsultantAttend's `directCountFilter ?? buildAttendedCountFilterQuery(
        // storedCountFilter)` fallback would keep re-applying it from Redux.
        if (onContactedCountFilter) onContactedCountFilter('');
        const values = normaliseValues(value).map((val) => val?.toString?.().toLowerCase?.()).filter(Boolean);
        if (!values.length || values.every((val) => val === '0')) {
            onWidgetClick([]);
            return;
        }
        onWidgetClick(resolveWidgetTreatments(treatments, value));
    };

    return (
        <>
            {dateRangeProps && <WidgetButtons
                toggle={toggle}
                dateRangeProps={dateRangeProps}
                handleOpen={handleOpen}
                handleClose={handleClose}
            />}
            {toggle && (
                <CustomWidgetCard handleWidgetClick={handleWidgetClick} widgetArray={widgetArray} currentWidget={currentWidget} />
            )}
        </>
    );
};

export default AttendedTableReportsWidget;

AttendedTableReportsWidget.propTypes = {
    onContactedCountFilter: PropTypes.func,
    onWidgetClick: PropTypes.func,
    dateRangeProps: PropTypes.any,
    widgetToggle: PropTypes.bool,
};
